import { Controller } from "@hotwired/stimulus";

/**
 * Click-to-edit for a single field on a record card.
 *
 * The field renders as plain text so a card reads as a card, not as a form. The
 * input only exists once the user asks for it, which is what keeps a dense record
 * legible: a screen of bordered boxes hides which values actually matter.
 *
 * Saving is left to HTMX on the form, so the server owns validation and returns the
 * re-rendered field. This controller only handles the display/edit swap and the
 * keyboard contract.
 */
export default class extends Controller<HTMLElement> {
    static targets = ["display", "form", "input"];

    declare readonly displayTarget: HTMLElement;
    declare readonly formTarget: HTMLElement;
    declare readonly inputTarget: HTMLInputElement | HTMLSelectElement;
    declare readonly hasFormTarget: boolean;
    declare readonly hasInputTarget: boolean;

    private originalValue = "";

    edit(event?: Event): void {
        event?.preventDefault();

        if (!this.hasFormTarget || !this.hasInputTarget) {
            return;
        }

        this.originalValue = this.inputTarget.value;
        this.displayTarget.classList.add("hidden");
        this.formTarget.classList.remove("hidden");
        this.inputTarget.focus();

        if (this.inputTarget instanceof HTMLInputElement) {
            this.inputTarget.select();
        }
    }

    cancel(): void {
        if (!this.hasFormTarget || !this.hasInputTarget) {
            return;
        }

        this.inputTarget.value = this.originalValue;
        this.formTarget.classList.add("hidden");
        this.displayTarget.classList.remove("hidden");
    }

    keydown(event: KeyboardEvent): void {
        if (event.key === "Escape") {
            event.preventDefault();
            this.cancel();
        }
    }

    /**
     * Blur closes the editor without saving. Submitting is deliberately explicit —
     * Enter or the confirm button — because a value that saves when focus wanders is
     * how a wrong amount ends up persisted without anyone deciding to persist it.
     */
    blur(event: FocusEvent): void {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && this.element.contains(nextTarget)) {
            return;
        }

        this.cancel();
    }
}
